import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useOutletContext } from 'react-router-dom';
import { normaliseRole, isAdminRole } from '../roles';
import { useModuleAdmin } from '../useModuleAdmin';
import { ShieldCheck, Save } from 'lucide-react';
import { useT } from '../i18n.jsx';

// The Settings tab for the five shared registers -- the calendar, timetable versions, the inventory
// catalogue, IT assets and campus facilities.
//
// These registers had no settings document at all, which meant no way to say "all teachers may add
// calendar events": writing them was system-administrator-only, and delegation meant promoting
// somebody to full administrator. This gives each one the same shape the duty modules already use --
// a role matrix in the module's own settings document, plus named individuals as module admins.
//
// Deliberately one component rather than five: the panel is identical in every case, and five copies
// would drift. The only thing that varies is which document it reads and what the register is called.
const ROLE_BUCKETS = ['Admin', 'Teacher', 'Staff'];

const ModuleEditPermissions = ({ settingsDoc, moduleId, recordLabel, approveLabel }) => {
  const { t } = useT();
  const { activeSchoolId, isMobile, userProfile } = useOutletContext();

  // The caller names its own records and its own approval step, in English. Those phrases get
  // slotted into whole sentences below, which reads fine in English and not at all in Chinese --
  // so they are looked up by moduleId, with the English the caller passed as the fallback. That
  // keeps a module with no entry yet showing the sentence it always showed.
  const records = t(`modulePermissions.records.${moduleId}`, {}, recordLabel);
  const action = t(`modulePermissions.actions.${moduleId}`, {}, approveLabel);
  const bucketLabel = (b) => t(`modulePermissions.buckets.${b.toLowerCase()}`, {}, b);
  const isAdmin = isAdminRole(normaliseRole(userProfile?.role));
  const isModuleAdmin = useModuleAdmin(activeSchoolId, moduleId);
  const canManage = isAdmin || isModuleAdmin;

  const [roles, setRoles] = useState([]);
  // Approving is a separate grant: whoever maintains the equipment catalogue is not necessarily who
  // signs off a loan request. Only rendered for modules that actually have an approval step.
  const [approveRoles, setApproveRoles] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [admins, setAdmins] = useState([]);

  useEffect(() => {
    if (!activeSchoolId) return;
    let cancelled = false;

    (async () => {
      setLoaded(false);
      try {
        const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', settingsDoc));
        const stored = snap.exists() ? (snap.data().editPermissions || {}).roles : null;
        if (!cancelled) setRoles(Array.isArray(stored) ? stored : []);
        const appr = snap.exists() ? (snap.data().approvePermissions || {}).roles : null;
        if (!cancelled) setApproveRoles(Array.isArray(appr) ? appr : []);
      } catch (e) {
        console.error('Error loading module permissions', e);
      }

      // Shown for reference. Editing it here would let a module admin add themselves elsewhere, so
      // the list itself is only writable from Access Control by a system administrator.
      try {
        const snap = await getDoc(doc(db, 'schools', activeSchoolId, 'metadata', 'module_admins'));
        if (!cancelled) setAdmins((snap.exists() ? snap.data()[moduleId] : null) || []);
      } catch (e) {
        console.error('Error loading module admins', e);
      }

      if (!cancelled) setLoaded(true);
    })();

    return () => { cancelled = true; };
  }, [activeSchoolId, settingsDoc, moduleId]);

  const makeToggle = (setter) => (bucket) => {
    if (bucket === 'Admin') return; // always granted, never removable
    setter((prev) => (prev.includes(bucket) ? prev.filter((r) => r !== bucket) : [...prev, bucket]));
  };
  const toggle = makeToggle(setRoles);
  const toggleApprove = makeToggle(setApproveRoles);

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(
        doc(db, 'schools', activeSchoolId, 'metadata', settingsDoc),
        {
          editPermissions: { roles },
          ...(approveLabel ? { approvePermissions: { roles: approveRoles } } : {}),
          updatedAt: new Date().toISOString(),
          updatedByEmail: (auth.currentUser?.email || '').trim().toLowerCase(),
        },
        { merge: true },
      );
      alert(t('modulePermissions.saved'));
    } catch (e) {
      console.error(e);
      alert(t('modulePermissions.saveFailed', { message: e.message }));
    }
    setSaving(false);
  };

  const card = {
    backgroundColor: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    borderRadius: isMobile ? 0 : 'var(--radius-lg)',
    padding: isMobile ? '0.9rem' : '1.5rem',
  };

  const renderMatrix = (selected, onToggle) => (
    <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      {ROLE_BUCKETS.map((bucket) => {
        const locked = bucket === 'Admin';
        const checked = locked || selected.includes(bucket);
        return (
          <label
            key={bucket}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.65rem',
              padding: '0.5rem 0.65rem', borderRadius: 'var(--radius-md)',
              cursor: locked || !canManage ? 'default' : 'pointer',
              backgroundColor: checked ? 'var(--color-primary-light)' : 'transparent',
              opacity: locked ? 0.7 : 1,
            }}
          >
            <input
              type="checkbox"
              checked={checked}
              disabled={locked || !canManage}
              onChange={() => onToggle(bucket)}
              style={{ width: '16px', height: '16px', accentColor: 'var(--color-primary)' }}
            />
            <span style={{ fontSize: '0.9rem' }}>
              {bucketLabel(bucket)}
              {locked && (
                <span style={{ color: 'var(--color-text-muted)', fontSize: '0.8rem' }}> {t('modulePermissions.alwaysSuffix')}</span>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );

  if (!loaded) {
    return (
      <div style={{ padding: '2rem', color: 'var(--color-text-muted)', fontSize: '0.9rem' }}>
        {t('modulePermissions.loadingPermissions')}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', padding: isMobile ? '0.75rem' : 0 }}>
      <div>
        <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: 0 }}>{t('modulePermissions.editTitle', { records })}</h2>
        {/* Two whole sentences rather than one sentence with a bolded fragment inside it. The three
            modules that already say where module admins are named quote the path instead of bolding
            it, and a sentence that survives translation is worth more than two bold words. */}
        <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0', maxWidth: '68ch', lineHeight: 1.7 }}>
          {t('modulePermissions.editIntro', { records })}{' '}{t('modulePermissions.namedInAdmins')}
        </p>
      </div>

      {renderMatrix(roles, toggle)}

      {approveLabel && (
        <>
          <div>
            <h2 style={{ fontSize: '1.25rem', fontWeight: 700, margin: '0.5rem 0 0' }}>{t('modulePermissions.approveTitle', { action })}</h2>
            <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem', margin: '0.35rem 0 0', maxWidth: '68ch', lineHeight: 1.7 }}>
              {t('modulePermissions.approveIntro', { action })}
            </p>
          </div>
          {renderMatrix(approveRoles, toggleApprove)}
        </>
      )}

      <div style={{ ...card, display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
          <ShieldCheck size={16} color="var(--color-primary)" />
          <strong style={{ fontSize: '0.9rem' }}>{t('modulePermissions.moduleAdminsCount', { count: admins.length })}</strong>
          <span style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
            {admins.length === 0 ? t('modulePermissions.noneNamed') : admins.join(', ')}
          </span>
        </div>
        <p style={{ fontSize: '0.8rem', color: 'var(--color-text-muted)', margin: 0, lineHeight: 1.7 }}>
          {t('modulePermissions.adminsReference')}
        </p>
      </div>

      {canManage && (
        <button
          className="btn-primary"
          onClick={handleSave}
          disabled={saving}
          style={{ width: 'auto', height: '42px', padding: '0 1.1rem', display: 'flex', alignItems: 'center', gap: '0.45rem', alignSelf: 'flex-start' }}
        >
          <Save size={16} />
          {saving ? t('common.saving') : t('common.save')}
        </button>
      )}

      {!canManage && (
        <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', margin: 0 }}>
          {t('modulePermissions.readOnly')}
        </p>
      )}
    </div>
  );
};

export default ModuleEditPermissions;
